/**
 * SetupCard — Simulador entry/setup screen.
 *
 * Three-step flow:
 *   1. Pick personality (1..9)
 *   2. Pick difficulty
 *   3. Pick procedure (default = aleatório; user may override manually)
 *
 * Bails out with an inline warning if the user has zero non-generic
 * procedures registered (start would fail at the edge function anyway).
 *
 * Spec: docs/architecture/simulador-spec.md §5 + §2.1.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PersonalityGrid } from "./PersonalityGrid";
import { DifficultyToggle } from "./DifficultyToggle";
import type { SimulatorDifficulty } from "../types";
import type { UserProcedureLite } from "../hooks/useUserProcedures";

const RANDOM = "__random__";

export interface SetupCardProps {
  procedures: UserProcedureLite[];
  isLoadingProcedures?: boolean;
  isStarting?: boolean;
  onStart: (
    eneatype: number,
    difficulty: SimulatorDifficulty,
    procedureId: string | null,
  ) => void;
}

function formatBRL(cents: number | null): string {
  if (cents == null) return "";
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

export function SetupCard({
  procedures,
  isLoadingProcedures,
  isStarting,
  onStart,
}: SetupCardProps) {
  const navigate = useNavigate();
  const [eneatype, setEneatype] = useState<number | null>(null);
  const [difficulty, setDifficulty] = useState<SimulatorDifficulty>("padrao");
  // Default = aleatório. User may override before starting.
  const [procedureId, setProcedureId] = useState<string>(RANDOM);

  const hasProcedures = procedures.length > 0;
  const canStart =
    !isStarting && !isLoadingProcedures && hasProcedures && eneatype !== null;

  return (
    <div className="space-y-8">
      {/* Step 1 — personality */}
      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold text-foreground">
            1. Escolha a personalidade do paciente
          </h2>
          <p className="text-xs text-muted-foreground">
            Cada eneatipo gera um paciente com voz e objeções próprias.
          </p>
        </header>
        <PersonalityGrid
          selected={eneatype}
          onSelect={setEneatype}
          disabled={isStarting}
        />
      </section>

      {/* Step 2 — difficulty */}
      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold text-foreground">
            2. Escolha a dificuldade
          </h2>
        </header>
        <DifficultyToggle
          value={difficulty}
          onChange={setDifficulty}
          disabled={isStarting}
        />
      </section>

      {/* Step 3 — procedure */}
      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold text-foreground">
            3. Procedimento da consulta
          </h2>
          <p className="text-xs text-muted-foreground">
            Por padrão sorteamos um dos seus procedimentos. Se quiser, escolha
            um específico.
          </p>
        </header>

        {isLoadingProcedures ? (
          <div className="rounded-xl border border-border/50 bg-card p-4 text-sm text-muted-foreground">
            Carregando procedimentos…
          </div>
        ) : hasProcedures ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle
                weight="duotone"
                className="w-5 h-5 text-emerald-500 shrink-0"
                aria-hidden="true"
              />
              <p className="text-sm text-foreground">
                {procedures.length}{" "}
                {procedures.length === 1
                  ? "procedimento cadastrado"
                  : "procedimentos cadastrados"}
                .
              </p>
            </div>
            <Select
              value={procedureId}
              onValueChange={setProcedureId}
              disabled={isStarting}
            >
              <SelectTrigger
                className="w-full bg-background"
                data-testid="setup-procedure-select"
              >
                <SelectValue placeholder="Aleatório" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={RANDOM}>
                  🎲 Aleatório (sorteia ao iniciar)
                </SelectItem>
                {procedures.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.value_cents != null ? ` — ${formatBRL(p.value_cents)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Warning
                weight="duotone"
                className="w-5 h-5 text-destructive shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-sm text-foreground">
                Cadastre ao menos um procedimento específico no seu perfil
                para iniciar uma simulação.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/perfil")}
              data-testid="setup-go-to-perfil"
            >
              Ir para Perfil
            </Button>
          </div>
        )}
      </section>

      {/* Start CTA */}
      <div className="pt-2">
        <Button
          size="lg"
          className="gap-2 glow-primary-sm w-full sm:w-auto"
          disabled={!canStart}
          onClick={() => {
            if (eneatype === null) return;
            const picked = procedureId === RANDOM ? null : procedureId;
            onStart(eneatype, difficulty, picked);
          }}
          data-testid="start-simulation"
        >
          {isStarting ? "Iniciando…" : "Iniciar simulação"}
          <ArrowRight weight="bold" className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
